<template>
	<li class="last-seen-hotel">
		<a
			class="last-seen-hotel__link"
			:href="url"
			rel="nofollow"
		>
			<div class="last-seen-hotel__image-wrapper">
				<img
					class="last-seen-hotel__img"
					:src="image"
					:alt="title"
				/>
			</div>
			<div class="last-seen-hotel__info">
				<h3 class="last-seen-hotel__heading">{{ title }}</h3>
				<HotelStars
					class="last-seen-hotel__rating"
					:rating="hotelRating"
					:hotel-box="false"
					:show-suns="showSuns"
				/>
				<span class="last-seen-hotel__region">{{ region }}</span>
			</div>
		</a>
	</li>
</template>

<script lang="ts" setup>
import HotelStars from '@lmt-rpb/HotelStars/HotelStars.vue';

interface Props {
	title: string
	image: string,
	url: string,
	region: string,
	hotelRating: number,
	showSuns: boolean
}

defineProps<Props>();

</script>

<style lang="scss" scoped>
.last-seen-hotel {
	list-style: none;
	height: 10rem;
	min-width: 28rem;
	max-width: 35rem;
	background-color: $color-white;
	box-shadow: $box-shadow-2;
	border-radius: $border-radius-large;
	transition: box-shadow 0.25s ease;

	&:hover {
		box-shadow: $box-shadow-2-hover;
	}

	&__link {
		min-height: 10rem;
		display: flex;
		text-decoration: none;
		height: 100%;
	}

	&__image {
		height: 12rem;

		&:focus {
			outline: none;
			opacity: 0.8;
		}
	}

	&__img {
		height: 100%;
		width: 15rem;
		object-fit: cover;
		border-top-left-radius: $border-radius-large;
		border-bottom-left-radius: $border-radius-large;
	}

	&__info {
		display: flex;
		flex-direction: column;
		padding-left: 1rem;
		padding-right: 0.6rem;
		width: 100%;
		overflow: hidden;
		overflow-wrap: break-word;
		background-color: $color-white;
		border-top-right-radius: $border-radius-large;
		border-bottom-right-radius: $border-radius-large;
	}

	&__heading {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		font-size: $font-small-1;
		text-align: left;
		font-weight: $font-weight-semibold;
		color: $color-primary;
		padding: 0;
		margin-top: 0.5rem;
		margin-bottom: 0.4rem;
		-webkit-line-clamp: 2;
		max-height:
			calc(
				1.6rem * 1.4 * 2
			); // font-size x line-height x number of lines

		overflow: hidden;
	}

	&__rating {
		text-align: left;
		height: 1.1rem;
	}

	&__region {
		text-align: left;
		margin: auto 0 0.8rem;
		color: $color-text;
		font-size: $font-small-3;
		width: 100%;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		max-height:
			calc(
				1.2rem * 1.7 * 1
			); // font-size x line-height x number of lines
	}
}

@media (min-width: $breakpoint-verysmall) {
	.last-seen-hotel {
		min-width: 30rem;
		height: 12rem;

		&__img {
			width: 18rem;
		}

		&__heading {
			font-size: $font-medium-3;
			max-height: calc(1.8rem * 1.4 * 2); // font-size x line-height x number of lines
		}

		&__region {
			white-space: initial;
			margin: auto 0 0.8rem;
			display: -webkit-box;
			max-height:
			calc(
				1.2rem * 1.7 * 2
			); // font-size x line-height x number of lines
		}
	}
}

@media (min-width: $breakpoint-small) {
	.last-seen-hotel {
		&__image {
			height: 12rem;
		}

		&__img {
			width: 18rem;
		}
	}
}

@media (min-width: $breakpoint-container) {
	.last-seen-hotel {
		min-width: 33rem;
	}
}

@media (min-width: $breakpoint-verylarge) {
	.last-seen-hotel {
		min-width: 35rem;

		&__overlay {
			display: none;
		}

		&__img {
			width: 18rem;
		}
	}
}

</style>
